import {
  BOOKING_STATUSES,
  STATUS_META,
  formatBookingDate,
  formatDateTime,
  statusMeta,
  typeLabel,
  type Booking,
  type BookingStatus,
} from "@/lib/bookings";

interface BookingsTableProps {
  bookings: Booking[];
  onSelect: (id: number) => void;
  onStatusChange: (id: number, status: BookingStatus) => void;
}

function StatusBadge({ status }: { status: string }) {
  const meta = statusMeta(status);
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border whitespace-nowrap ${meta.badge}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`}></span>
      {meta.label}
    </span>
  );
}

function StatusSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (status: BookingStatus) => void;
}) {
  return (
    <select
      value={value}
      onClick={(event) => event.stopPropagation()}
      onChange={(event) => onChange(event.target.value as BookingStatus)}
      className="bg-background-100 border border-background-300 rounded-md px-2.5 py-2 text-xs text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
    >
      {BOOKING_STATUSES.map((status) => (
        <option key={status} value={status}>
          {STATUS_META[status].label}
        </option>
      ))}
    </select>
  );
}

export default function BookingsTable({
  bookings,
  onSelect,
  onStatusChange,
}: BookingsTableProps) {
  return (
    <>
      {/* Desktop table */}
      <div className="hidden md:block bg-background-50 border border-background-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-background-100 border-b border-background-200">
                <th className="px-4 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Guest</th>
                <th className="px-4 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Type</th>
                <th className="px-4 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Booking</th>
                <th className="px-4 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Preferred</th>
                <th className="px-4 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Group</th>
                <th className="px-4 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Received</th>
                <th className="px-4 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200">
              {bookings.map((booking) => (
                <tr
                  key={booking.id}
                  onClick={() => onSelect(booking.id)}
                  className="hover:bg-background-100 transition-colors cursor-pointer align-top"
                >
                  <td className="px-4 py-4">
                    <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">
                      {booking.name}
                    </p>
                    <p className="text-xs text-foreground-500 mt-0.5">{booking.email}</p>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-[11px] font-semibold text-secondary-700 bg-secondary-100 border border-secondary-200 px-2.5 py-1 rounded-full whitespace-nowrap">
                      {typeLabel(booking.booking_type)}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <p className="text-sm text-foreground-800 max-w-[220px] truncate">
                      {booking.subject || "—"}
                    </p>
                    {booking.itinerary ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-accent-700 mt-1">
                        <i className="ri-route-line"></i> Itinerary attached
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-4 text-sm text-foreground-700 whitespace-nowrap">
                    {formatBookingDate(booking.preferred_date)}
                  </td>
                  <td className="px-4 py-4 text-sm text-foreground-700">
                    {booking.group_size ? `${booking.group_size} pax` : "—"}
                  </td>
                  <td className="px-4 py-4 text-xs text-foreground-500 whitespace-nowrap">
                    {formatDateTime(booking.created_at)}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex flex-col items-start gap-2">
                      <StatusBadge status={booking.status} />
                      <StatusSelect
                        value={booking.status}
                        onChange={(status) => onStatusChange(booking.id, status)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {bookings.map((booking) => (
          <div
            key={booking.id}
            className="bg-background-50 border border-background-200 rounded-lg p-4"
          >
            <button
              type="button"
              onClick={() => onSelect(booking.id)}
              className="w-full text-left cursor-pointer"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground-950 truncate">
                    {booking.name}
                  </p>
                  <p className="text-xs text-foreground-500 mt-0.5 truncate">{booking.email}</p>
                </div>
                <StatusBadge status={booking.status} />
              </div>
              <p className="text-sm text-foreground-800 mt-3 line-clamp-2">
                {booking.subject || "—"}
              </p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-foreground-500">
                <span className="inline-flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {formatBookingDate(booking.preferred_date)}
                </span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-group-line"></i>
                  {booking.group_size ? `${booking.group_size} pax` : "—"}
                </span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-price-tag-3-line"></i>
                  {typeLabel(booking.booking_type)}
                </span>
              </div>
            </button>
            <div className="flex items-center justify-between gap-3 mt-4 pt-3 border-t border-background-200">
              <span className="text-[11px] text-foreground-400">
                {formatDateTime(booking.created_at)}
              </span>
              <StatusSelect
                value={booking.status}
                onChange={(status) => onStatusChange(booking.id, status)}
              />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}